import type maplibregl from 'maplibre-gl';

import { ORBIT_LAYER_IDS } from './mapLayerOrder';
import type { MapLayerGroup } from './mapLayerOrder';

export type FeatureOverlayKey =
    | 'gridTiles'
    | 'districtMeterAreas'
    | 'surveyedAreas'
    | 'pipelines'
    | 'fittings'
    | 'technicianVans'
    | 'airValves'
    | 'highRiskAirValves';

/**
 * The products that share the Orbit MapView, plus Network Explorer — the cross-client viewing
 * mode, which is a map mode rather than a product: it has no module and no client of its own.
 * Drives mode-specific controls/layers.
 */
export type MapMode = 'find-and-fix' | 'savi' | 'network-explorer';

export type PoiTypeKey =
    | 'leakFound'
    | 'noLeakFound'
    | 'inconclusive'
    | 'awaitingAssessment'
    | 'alreadyMarkedUp'
    | 'unableToCheck';

export type FeatureOverlayState = Record<FeatureOverlayKey, boolean>;
export type PoiTypeState = Record<PoiTypeKey, boolean>;

export interface FeatureOverlayDef {
    key: FeatureOverlayKey;
    label: string;
    group: MapLayerGroup;
    /** MapLibre layer IDs controlled by this toggle. Empty until real layers exist. */
    mapLayerIds: string[];
    defaultEnabled: boolean;
}

export interface PoiTypeDef {
    key: PoiTypeKey;
    label: string;
    group: MapLayerGroup;
    /**
     * Stable property value identifying this POI type in the map data.
     * Used when POIs are implemented as a single shared layer with a filter expression.
     */
    propertyValue: string;
    /** MapLibre layer IDs controlled by this toggle. Used when POIs use separate layers per type. */
    mapLayerIds: string[];
    defaultEnabled: boolean;
}

export const FEATURE_OVERLAYS: FeatureOverlayDef[] = [
    {
        key: 'gridTiles',
        label: 'Grid Tiles',
        group: 'areaOverlays',
        mapLayerIds: [ORBIT_LAYER_IDS.gridTilesFill, ORBIT_LAYER_IDS.gridTilesOutline, ORBIT_LAYER_IDS.gridTilesLabels],
        defaultEnabled: true,
    },
    {
        key: 'districtMeterAreas',
        label: 'District Meter Areas (DMAs)',
        group: 'areaOverlays',
        mapLayerIds: [ORBIT_LAYER_IDS.dmaFill, ORBIT_LAYER_IDS.dmaOutline, ORBIT_LAYER_IDS.dmaLabels],
        defaultEnabled: true,
    },
    {
        key: 'surveyedAreas',
        label: 'Surveyed Areas',
        group: 'areaOverlays',
        mapLayerIds: [
            ORBIT_LAYER_IDS.surveyedAreasFill,
            ORBIT_LAYER_IDS.surveyedAreasOutline,
            ORBIT_LAYER_IDS.areaLabels,
        ],
        defaultEnabled: true,
    },
    {
        key: 'pipelines',
        label: 'Pipelines',
        group: 'lineOverlays',
        mapLayerIds: [ORBIT_LAYER_IDS.pipelinesCase, ORBIT_LAYER_IDS.pipelines],
        defaultEnabled: true,
    },
    {
        key: 'fittings',
        label: 'Fittings',
        group: 'assetOverlays',
        mapLayerIds: [ORBIT_LAYER_IDS.fittingsShadow, ORBIT_LAYER_IDS.fittings],
        defaultEnabled: true,
    },
    {
        key: 'technicianVans',
        label: 'Technician Vans',
        group: 'assetOverlays',
        mapLayerIds: [
            ORBIT_LAYER_IDS.technicianVanHaloOuter,
            ORBIT_LAYER_IDS.technicianVanHaloInner,
            ORBIT_LAYER_IDS.technicianVans,
        ],
        defaultEnabled: true,
    },
    // SAVI-only overlays. Present in the master list so FeatureOverlayState/defaults always
    // include them and useMapLayerVisibility manages them; they only appear in the Layers panel
    // under SAVI mode (see getFeatureOverlaysForMode). The two share one source but separate
    // layers (split by the `highRisk` property) so each toggles independently.
    {
        key: 'airValves',
        label: 'Air Valves',
        group: 'pois',
        mapLayerIds: [
            ORBIT_LAYER_IDS.airValves,
            ORBIT_LAYER_IDS.airValvesClusters,
            ORBIT_LAYER_IDS.airValvesClusterCount,
        ],
        defaultEnabled: true,
    },
    {
        key: 'highRiskAirValves',
        label: 'High Risk Air Valves',
        group: 'pois',
        mapLayerIds: [
            ORBIT_LAYER_IDS.airValvesHighRisk,
            ORBIT_LAYER_IDS.airValvesHighRiskClusters,
            ORBIT_LAYER_IDS.airValvesHighRiskClusterCount,
        ],
        defaultEnabled: true,
    },
];

/** Feature-overlay keys that belong only to the SAVI product (hidden in Find & Fix). */
const SAVI_ONLY_OVERLAY_KEYS: ReadonlySet<FeatureOverlayKey> = new Set(['airValves', 'highRiskAirValves']);

/** The SAVI Layers "Features" list, in design order. */
const SAVI_FEATURE_OVERLAY_KEYS: FeatureOverlayKey[] = [
    'districtMeterAreas',
    'pipelines',
    'fittings',
    'airValves',
    'highRiskAirValves',
];

const FEATURE_OVERLAYS_BY_KEY: Record<FeatureOverlayKey, FeatureOverlayDef> = Object.fromEntries(
    FEATURE_OVERLAYS.map((d) => [d.key, d]),
) as Record<FeatureOverlayKey, FeatureOverlayDef>;

/**
 * Returns the Layers-panel "Features" list for the given mode, in display order.
 * SAVI shows DMAs/Pipelines/Fittings/Air Valves/High Risk Air Valves; Find & Fix shows
 * the full standard overlay set (excluding the SAVI-only air-valve overlays).
 * Technician Vans additionally requires the per-user vehicle-tracking capability —
 * without it the row is absent entirely, never shown as a dead toggle.
 * Network Explorer spans every client, so only estate-wide layers make sense there: the
 * fleet is the one such overlay; grid tiles, AOIs, DMAs, pipes and fittings are all
 * single-client data and are never offered.
 */
export function getFeatureOverlaysForMode(mode: MapMode, canViewVehicleTracking: boolean): FeatureOverlayDef[] {
    if (mode === 'savi') {
        return SAVI_FEATURE_OVERLAY_KEYS.map((key) => FEATURE_OVERLAYS_BY_KEY[key]);
    }
    if (mode === 'network-explorer') {
        return canViewVehicleTracking ? [FEATURE_OVERLAYS_BY_KEY.technicianVans] : [];
    }
    return FEATURE_OVERLAYS.filter(
        (d) => !SAVI_ONLY_OVERLAY_KEYS.has(d.key) && (d.key !== 'technicianVans' || canViewVehicleTracking),
    );
}

/**
 * Returns a copy of `state` with every overlay that does not belong to `mode` (or that the
 * user lacks the capability for) forced off, leaving in-mode toggles untouched. Guarantees
 * e.g. Grid Tiles / Surveyed Areas can never render in SAVI — and Technician Vans can never
 * render without the vehicle-tracking capability — even if a persisted/URL toggle is on.
 * The raw state (for the Layers panel and persistence) is unaffected — this is a
 * render-time derivation only.
 */
export function getEffectiveFeatureOverlays(
    state: FeatureOverlayState,
    mode: MapMode,
    canViewVehicleTracking: boolean,
): FeatureOverlayState {
    const allowed = new Set(getFeatureOverlaysForMode(mode, canViewVehicleTracking).map((d) => d.key));
    return Object.fromEntries(
        FEATURE_OVERLAYS.map((d) => [d.key, allowed.has(d.key) && state[d.key]]),
    ) as FeatureOverlayState;
}

export const POI_TYPES: PoiTypeDef[] = [
    {
        key: 'leakFound',
        label: 'Leak Found',
        group: 'pois',
        propertyValue: 'leak_found',
        mapLayerIds: [],
        defaultEnabled: true,
    },
    {
        key: 'noLeakFound',
        label: 'No Leak Found',
        group: 'pois',
        propertyValue: 'no_leak_found',
        mapLayerIds: [],
        defaultEnabled: true,
    },
    {
        key: 'inconclusive',
        label: 'Inconclusive',
        group: 'pois',
        propertyValue: 'inconclusive',
        mapLayerIds: [],
        defaultEnabled: true,
    },
    {
        key: 'awaitingAssessment',
        label: 'Awaiting Assessment',
        group: 'pois',
        propertyValue: 'awaiting_assessment',
        mapLayerIds: [],
        defaultEnabled: true,
    },
    {
        key: 'alreadyMarkedUp',
        label: 'Already Marked Up',
        group: 'pois',
        propertyValue: 'already_marked_up',
        mapLayerIds: [],
        defaultEnabled: true,
    },
    {
        key: 'unableToCheck',
        label: 'Unable to Check',
        group: 'pois',
        propertyValue: 'unable_to_check',
        mapLayerIds: [],
        defaultEnabled: true,
    },
    // Note: "No Dig Repairs" is NOT a POI status — it is a separate boolean (`nodig_fix`)
    // orthogonal to leakage result. It is filtered via MapFilterState.noDigRepairs, not here.
];

// ─── Entity toggles (the split POI/leak model's Layers-panel controls) ─────────────────
//
// The Layers panel's per-status POI section is superseded by two entity-level toggles:
// "POIs" and "Leaks", each hiding its whole family at source level (so cluster counts
// follow). Per-status control lives solely in the Filters panel. POI_TYPES above remains
// only while the pre-split pipeline still exists and is removed with it.

export interface EntityToggleState {
    pois: boolean;
    leaks: boolean;
}

export interface EntityToggleDef {
    key: keyof EntityToggleState;
    label: string;
    defaultEnabled: boolean;
}

export const ENTITY_TOGGLES: EntityToggleDef[] = [
    { key: 'pois', label: 'POIs', defaultEnabled: true },
    { key: 'leaks', label: 'Leaks', defaultEnabled: true },
];

export const DEFAULT_ENTITY_TOGGLE_STATE: EntityToggleState = { pois: true, leaks: true };

export const DEFAULT_FEATURE_OVERLAY_STATE: FeatureOverlayState = Object.fromEntries(
    FEATURE_OVERLAYS.map((d) => [d.key, d.defaultEnabled]),
) as FeatureOverlayState;

export const DEFAULT_POI_TYPE_STATE: PoiTypeState = Object.fromEntries(
    POI_TYPES.map((d) => [d.key, d.defaultEnabled]),
) as PoiTypeState;

/** No-ops if the layer does not exist or the call throws — safe before real layers are added. */
export function setLayerVisibility(map: maplibregl.Map, layerId: string, visible: boolean): void {
    if (!map.getLayer(layerId)) return;
    try {
        map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
    } catch {
        // Progressive enhancement — a missing or not-yet-loaded layer must not break the map.
    }
}
